import {useCallback, useState} from 'react';

import type {UserFacingEntity} from '../../../../../../ui/core-entity/rows/peep-rows/types';

import {CoreEntityConfirmationDialog} from '../../core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import type {CoreEntityConfirmationType} from '../../core-entity-confirmation-dialog/types';

type DeleteConfirmationResult = {
    requestDelete: () => void;
    isDeleteConfirmOpen: boolean;
    deleteConfirmationDialog: React.ReactNode;
    deleteAnchorRef: (el?: HTMLElement | null | undefined) => void;
};

export function useDeleteConfirmation(
    onDelete: () => Promise<any>,
    setIsMutating: (isMutating: boolean) => void,
    rowType: CoreEntityConfirmationType,
    parentEntity: UserFacingEntity
): DeleteConfirmationResult {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const requestDelete = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        setIsOpen(false);
        setIsMutating(true);
        onDelete().finally(() => setIsMutating(false));
    }, [onDelete, setIsMutating]);

    const handleCancel = useCallback(() => {
        setIsOpen(false);
    }, []);

    const deleteAnchorRef = useCallback((el?: HTMLElement | null | undefined) => {
        setAnchor(el ?? null);
    }, []);

    const deleteConfirmationDialog = isOpen && (
        <CoreEntityConfirmationDialog
            anchor={anchor}
            type={rowType}
            parentEntity={parentEntity}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    );

    return {
        requestDelete,
        isDeleteConfirmOpen: isOpen,
        deleteConfirmationDialog,
        deleteAnchorRef,
    };
}
